import React, { useState, useEffect } from 'react';
import tw from 'twin.macro';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { calcularPrecioConDescuento } from 'utils/descuentoUtils';
import { getMensajeBloqueoMezclaCarrito, handleAddToCart } from 'utils/carritoUtils';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast } from 'react-toastify';
import { obtenerAdicionales } from 'scripts/obtenerAdicionales';

const Content = tw.div`flex flex-col lg:flex-row items-center`;
const ImageContainer = styled.div`
	${tw`flex-shrink-0 w-full lg:w-1/2 rounded-lg overflow-hidden shadow-lg relative bg-gray-200`}
	min-height: 400px;
`;

const Image = styled.img`
	${tw`w-full h-full object-cover`}
	max-height: 400px;
	transition: opacity 0.5s ease-in-out;
	opacity: ${(props) => (props.isLoaded ? 1 : 0)};
`;

const ImagePlaceholder = styled.div`
	${tw`absolute inset-0 flex items-center justify-center bg-gray-200`}
	animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;

	@keyframes pulse {
		0%,
		100% {
			opacity: 1;
		}
		50% {
			opacity: 0.5;
		}
	}
`;
const Details = tw.div`mt-6 lg:mt-0 lg:ml-8 w-full lg:w-1/2`;
const Title = tw.h1`text-3xl font-bold text-primary-500 mb-4`;
const Description = tw.p`text-lg text-gray-700 mb-6 leading-relaxed`;
const PriceContainer = tw.div`mb-4 flex flex-col`;
const OriginalPrice = styled.p`
	${tw`text-lg font-medium line-through text-red-500`}
`;
const DiscountedPrice = styled.p`
	${tw`text-2xl font-semibold text-gray-900`}
`;
const DiscountList = styled.ul`
	${tw`mt-4`}
`;
const DiscountItem = styled.li`
	${tw`text-sm text-green-600`}
`;
const ButtonContainer = tw.div`flex space-x-4 mt-4`;
const QuantityContainer = tw.div`flex items-center space-x-4`;

const QuantityButton = styled.button`
	${tw`px-3 py-1 bg-gray-200 text-gray-700 font-medium rounded-md hover:bg-gray-300`}
`;

const QuantityInput = styled.input`
	${tw`w-12 text-center border border-gray-300 rounded-md`}
`;

const SelectContainer = tw.div`flex flex-col mt-4`;
const SelectLabel = tw.label`text-sm font-medium text-gray-700 mb-2`;
const Select = styled.select`
	${tw`px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500`}
`;

const PrimaryButton = styled.button`
	${tw`px-6 py-3 bg-primary-500 text-white font-medium rounded-lg shadow-lg`}
	&:hover {
		${tw`bg-primary-700`}
	}
`;

const BackButton = styled.button`
	${tw`flex items-center space-x-2 text-primary-500 font-semibold mb-4`}
	&:hover {
		${tw`text-primary-700`}
	}
`;

const Producto = ({ producto }) => {
	const dispatch = useDispatch();
	const token = sessionStorage.getItem('token');
	const user = useSelector((state) => state.usuario);
	const cantidadProductosCarrito = useSelector((state) => state.carrito.cantidadProductos);
	const cartItems = useSelector((state) => state.carrito.items || []);
	let clienteId = null;
	if (user.isLoggedIn) {
		clienteId = user.userInfo.id;
	}

	// Usar imagen_full si existe, sino imagen original
	const imageUrl =
		producto.imagen_full ||
		producto.imagen ||
		'https://images.unsplash.com/photo-1455619452474-d2be8b1e70cd?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80';

	const [cantidad, setCantidad] = useState(1);
	const [adicionales, setAdicionales] = useState([]);
	const [selectedAdicional, setSelectedAdicional] = useState(null);
	const [precioTotalConAdicionales, setPrecioTotalConAdicionales] = useState(0);
	const [imageLoaded, setImageLoaded] = useState(false);

	const precioConDescuento = calcularPrecioConDescuento(producto.precio, producto.descuentos || []);

	// Calcular precio total incluyendo adicionales
	const calcularPrecioTotal = (precioBase, adicionalId, listaAdicionales) => {
		let costoAdicional = 0;

		if (adicionalId && adicionalId !== 0 && adicionalId !== '0') {
			const adicionalSeleccionado = listaAdicionales.find((a) => a.id === parseInt(adicionalId));
			if (adicionalSeleccionado) {
				costoAdicional = adicionalSeleccionado.costoAdicional || 0;
			}
		}

		setPrecioTotalConAdicionales(precioBase + costoAdicional);
	};

	useEffect(() => {
		const fetchAdicionales = async () => {
			try {
				obtenerAdicionales(producto.id).then((data) => {
					if (data.length === 0) {
						setSelectedAdicional(0);
					}
					setAdicionales(data);
					// Calcular precio inicial (sin adicionales)
					calcularPrecioTotal(precioConDescuento, null, data);
				});
			} catch (error) {
				setSelectedAdicional(null);
				console.error('Error al obtener adicionales:', error);
				// Calcular precio inicial incluso si hay error
				calcularPrecioTotal(precioConDescuento, null, []);
			}
		};
		fetchAdicionales();
	}, [producto.id, dispatch, token, precioConDescuento]);

	const handleAgregarACarrito = () => {
		const mensajeBloqueo = getMensajeBloqueoMezclaCarrito(cartItems, 'producto');
		if (mensajeBloqueo) {
			toast.info(mensajeBloqueo);
			return;
		}

		if (selectedAdicional === null) {
			toast.error('Debe elegir un adicional.');
		} else {
			handleAddToCart(
				producto,
				cantidad,
				selectedAdicional,
				token,
				clienteId,
				'producto',
				dispatch,
				cantidadProductosCarrito,
			)
				.then(({ primerProductoAgregado }) => {
					toast.success('Producto agregado al carrito.');
					if (primerProductoAgregado) {
						toast.info('Tu carrito se guarda por 24 horas. Luego se limpiará automáticamente.');
					}
				})
				.catch((e) => {
					console.log(e.message);
				});
		}
	};

	const incrementarCantidad = () => {
		setCantidad((prevCantidad) => prevCantidad + 1);
	};

	const decrementarCantidad = () => {
		setCantidad((prevCantidad) => (prevCantidad > 1 ? prevCantidad - 1 : 1));
	};

	const handleCantidadChange = (e) => {
		const value = parseInt(e.target.value, 10);
		setCantidad(isNaN(value) || value <= 0 ? 1 : value);
	};

	const handleSelectChange = (e) => {
		const adicionalId = e.target.value;
		setSelectedAdicional(adicionalId);
		// Recalcular precio con el adicional seleccionado
		calcularPrecioTotal(precioConDescuento, adicionalId, adicionales);
	};

	const navigate = useNavigate();

	return (
		<Content>
			<BackButton onClick={() => navigate('/productos')}>
				<ArrowBackIcon fontSize="small" /> <span>Volver a productos</span>
			</BackButton>
			<ImageContainer>
				{!imageLoaded && (
					<ImagePlaceholder>
						<span tw="text-gray-400">Cargando imagen...</span>
					</ImagePlaceholder>
				)}
				<Image
					src={imageUrl}
					alt={producto.titulo}
					isLoaded={imageLoaded}
					onLoad={() => setImageLoaded(true)}
					onError={(e) => {
						// Fallback a imagen por defecto si falla
						e.target.src =
							'https://images.unsplash.com/photo-1455619452474-d2be8b1e70cd?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80';
						setImageLoaded(true);
					}}
				/>
			</ImageContainer>
			<Details>
				<Title>{producto.titulo || 'Producto sin título'}</Title>
				<Description>{producto.descripcion || 'Este producto no tiene descripción disponible.'}</Description>
				<PriceContainer>
					{producto.descuentos && producto.descuentos.length > 0 && (
						<>
							<OriginalPrice>${producto.precio.toFixed(2)}</OriginalPrice>
							<DiscountedPrice>
								$
								{precioTotalConAdicionales > 0
									? precioTotalConAdicionales.toFixed(2)
									: precioConDescuento.toFixed(2)}
							</DiscountedPrice>
						</>
					)}
					{(!producto.descuentos || producto.descuentos.length === 0) && (
						<DiscountedPrice>
							$
							{precioTotalConAdicionales > 0
								? precioTotalConAdicionales.toFixed(2)
								: producto.precio.toFixed(2)}
						</DiscountedPrice>
					)}
				</PriceContainer>
				{producto.descuentos && producto.descuentos.length > 0 && (
					<DiscountList>
						{producto.descuentos.map((item) => (
							<DiscountItem key={item.id}>
								{item.descuento.descripcion} -{' '}
								{item.descuento.tipo === 'PORCENTAJE'
									? `${(item.descuento.valor * 100).toFixed(2)}%`
									: `$${item.descuento.valor.toFixed(2)}`}
							</DiscountItem>
						))}
					</DiscountList>
				)}
				<QuantityContainer>
					<QuantityButton onClick={decrementarCantidad}>-</QuantityButton>
					<QuantityInput type="number" value={cantidad} onChange={handleCantidadChange} min="1" />
					<QuantityButton onClick={incrementarCantidad}>+</QuantityButton>
				</QuantityContainer>
				{adicionales.length > 0 && (
					<SelectContainer>
						<SelectLabel>Selecciona un adicional:</SelectLabel>
						<Select value={selectedAdicional} onChange={handleSelectChange}>
							<option selected={true} value={0} disabled={true}>
								Seleccionar
							</option>
							{adicionales &&
								adicionales.map((adicional) => (
									<option key={adicional.id} value={adicional.id}>
										{adicional.descripcion}
										{adicional.costoAdicional > 0 && ` (+$${adicional.costoAdicional})`}
									</option>
								))}
						</Select>
					</SelectContainer>
				)}
				<ButtonContainer>
					<PrimaryButton onClick={handleAgregarACarrito}>Agregar al carrito</PrimaryButton>
				</ButtonContainer>
			</Details>
		</Content>
	);
};

export default Producto;
